/*****************************************************************************
 *
 * Methods to create and download CSV, SVG, and PNG images for this project
 *
 *****************************************************************************/

import _ from 'lodash';
import 'canvas-toBlob';
import FileSaver from 'file-saver';

/**
 * Save a string as a text file
 * Uses Blob and saveAs browser functionality
 *
 * @param text
 * @param filename
 */
export const saveTextFile = (text, filename) => {
  const blob = new Blob([text], {type: 'text/plain;charset=utf-8"'});
  FileSaver.saveAs(blob, filename, true);
}

/**
 * Render svg text to offscreen canvas and save exported png image
 *
 * @param svgText
 * @param filename
 * @param width
 * @param height
 */
export const saveSvgTextImage = (svgText, filename, width, height) => {
  renderCanvasPng(svgText, {width, height})
    .then(file => FileSaver.saveAs(file, filename, true))
}

/**
 * Creates a string representation of a csv file from data and columns.
 *
 * @param data
 * @param columns
 * @param footers
 * @returns {string}
 */
export const createCsv = (data, columns, footers) => {
  const separator = ',';
  const linebreak = '\n';

  // Create headers from column labels
  const headers = _.map(columns, 'label');

  // Create headers row in csv text
  let csv = _.join(headers, separator) + linebreak;

  // Loop through data to create rows array of arrays
  const rowValues = _.map(data, row => {
    return _.map(columns, c => _.get(row, c.field) || '')
  });

  // Push values out with commas and new line
  _.forEach(rowValues, (rowArray) => {
    if (rowArray && rowArray.length) {
      rowArray = _.map(rowArray, (item) => {
        return _.isNumber(item) ? item : `"${item}"`;
      });

      csv += _.join(rowArray, separator) + linebreak;
    }
  });

  // Add footer rows
  _.forEach(footers, (footer) => {
    csv += `${linebreak}"${footer}"`;
  });

  return csv;
}

/**
 * Renders svg text into a canvas with 2x scale and extracts
 * a png data file from the canvas after rendering.
 *
 * @param svgText
 * @param svgSize
 * @returns {Promise<unknown>}
 */
const renderCanvasPng = async (svgText, svgSize) => {
  const img = document.createElement('img');
  const canvas = document.createElement('canvas');
  const svgDataUrl = btoa(unescape(encodeURIComponent(svgText)))

  const scale = 2;
  const margin = 20

  const width = svgSize.width + margin * 2
  const height = svgSize.height + margin * 2

  //Resize can break shadows
  canvas.width = width * scale;
  canvas.height = height * scale;
  canvas.style.width = width;
  canvas.style.height = height;
  canvas.className = 'exporting'

  const context = canvas.getContext('2d');
  context.scale(scale, scale);

  context.fillStyle = "white";
  context.fillRect(0, 0, canvas.width, canvas.height);

  return new Promise(resolve => {
    img.onload = () => {
      try {
        context.drawImage(img, margin, margin)
        canvas.toBlob(blob => resolve(blob))
      }
      catch (error) {
        console.log("canvas to image error", error)
        resolve(null);
      }
    };
    img.setAttribute('crossOrigin', '');
    img.setAttribute('src', 'data:image/svg+xml;base64,' + svgDataUrl);
  });
};
